import React, { useContext, createContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getDisplayName } from 'recompose';

import { shortcodeToLocale, getLanguageDisplayName, getLanguageCodeFromLocale } from 'helpers/localization.js';
import { FirebaseContext } from 'components/Firebase/index.js';
import { FIREBASE_AUTH_ERROR_MESSAGES } from 'constants/internalDefaultTranslations.js';
import DASHBOARD_WEB from 'constants/keys/default.json';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import useLocaleMomentAndDateFNS from 'hooks/useLocaleMomentAndDateFNS.js';
import { useUser } from 'contexts/UserContext.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import useOrgGeneralSettings from 'pages/GeneralSettings/useOrgGeneralSettings.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import { localeDate, localeDateTime, localeTime } from 'utils/index.js';

const getDefaultTranslations = () => {
  const dashboard = DASHBOARD_WEB ?? {};
  const firebaseErrors = Object.entries(FIREBASE_AUTH_ERROR_MESSAGES).reduce((acc, [label, { text }]) => {
    acc[label] = text;
    return acc;
  }, {});

  return { dashboard, firebaseErrors };
};

const DEFAULT_TRANSLATIONS = getDefaultTranslations();
export const DEFAULT_LOCALE = 'en-US';
export const SUPPORTED_LOCALES = {
  'en-GB': {
    available: false,
    locale: 'en-GB',
  },
  'en-US': {
    available: true,
    locale: 'en-US',
  },
  'pt-BR': {
    available: false,
    locale: 'pt-BR',
  },
  'fr-FR': {
    available: true,
    locale: 'fr-FR',
  },
  'es-ES': {
    available: true,
    locale: 'es-ES',
  },
  'ja-JP': {
    available: true,
    locale: 'ja-JP',
    script: 'Jpan',
  },
};

const getLanguage = (locale) => {
  if (SUPPORTED_LOCALES[locale]?.script) {
    return getLanguageDisplayName(locale);
  }
  return getLanguageCodeFromLocale(locale);
};

/**
 * Replaces placeholders in a string with values from a data object.
 * If a value in the data object is a React component, it's added to the output array.
 * @param {string} string - The string to search for placeholders.
 * @param {Object.<string, (string|ReactElement)>} data - The data object containing values to replace placeholders with.
 * @returns {(string|Array)} - The replaced string or an array of strings and React components.
 */
export const findAndReplace = (string, data) => {
  const hasReactElements = Object.values(data).some((value) => React.isValidElement(value));

  const strings = Object.entries(data).filter(([key, value]) => !React.isValidElement(value));

  const replacedString = strings.reduce((acc, [key, value]) => {
    return acc.replace(new RegExp(`{${key}}`, 'g'), value);
  }, string);

  if (!hasReactElements) return replacedString;

  return replacedString.split(/({[^}]+})/g).map((part) => {
    if (!part.startsWith('{') || !part.endsWith('}')) return part;

    const key = part.slice(1, -1);

    if (!data[key]) return part;

    return React.cloneElement(data[key], { key });
  });
};

const getFallBackLabel = (key, data, fallbackLabel) => {
  if (fallbackLabel) {
    return fallbackLabel;
  } else if (process.env.NODE_ENV === 'test' && data && Object.keys(data).length) {
    return `${key}${JSON.stringify(data)}`;
  } else if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    return key;
  }
  return '';
};

const factory = {};
// Used to generate the function for resolving labels for a given translation
factory.getText = (translations = DEFAULT_TRANSLATIONS) => (labelKey, data = {}, fallbackLabel) => {
  const label = translations.dashboard[labelKey] || DEFAULT_TRANSLATIONS.dashboard[labelKey] || getFallBackLabel(labelKey, data, fallbackLabel);
  return findAndReplace(label, data);
};

// Used to generate the function for resolving translated firebaseErrors for a given translation
factory.getErrorMessage = (translations = DEFAULT_TRANSLATIONS) => (errorCode, errorMessage = '') => {
  const label = translations.firebaseErrors[errorCode] || DEFAULT_TRANSLATIONS.firebaseErrors[errorCode];

  if (label === undefined) {
    console.error('Tried to load translation which doesn\'t exist', { errorCode });
  }

  return label || errorMessage;
};

factory.t = (translations = DEFAULT_TRANSLATIONS) => (key = '', data, fallbackLabel) => {
  const label = translations.dashboard[key] || DEFAULT_TRANSLATIONS.dashboard[key] || getFallBackLabel(key, data, fallbackLabel);
  if (typeof data === 'object') {
    return findAndReplace(label, data);
  }
  return label;
};

factory.localeDate = (dateFNSLocale) => (date, format) => {
  return localeDate(date, format, dateFNSLocale);
};

factory.localeDateTime = (dateFNSLocale) => (date) => {
  return localeDateTime(date, dateFNSLocale);
};

factory.localeTime = (dateFNSLocale) => (date) => {
  return localeTime(date, dateFNSLocale);
};

/* --------------------------------------CONTEXT BEGIN----------------------------------------------- */
export const LocalizationContext = createContext({
  loading: false,
  locale: DEFAULT_LOCALE,
  translations: DEFAULT_TRANSLATIONS,
  getText: factory.getText(DEFAULT_TRANSLATIONS),
  getErrorMessage: factory.getErrorMessage(DEFAULT_TRANSLATIONS),
  t: factory.t(DEFAULT_TRANSLATIONS),
  momentLocale: DEFAULT_LOCALE,
  dateFNSLocale: DEFAULT_LOCALE,
  localeDate: factory.localeDate,
  localeDateTime: factory.localeDateTime,
  localeTime: factory.localeTime,
}); // Contains data and methods specific to the locale of the client.
LocalizationContext.displayName = 'LocalizationContext'; // React devtools display names

const getBrowserLocale = () => {
  const locale = window.navigator.userLanguage || window.navigator.language;
  return (SUPPORTED_LOCALES[locale] || SUPPORTED_LOCALES[shortcodeToLocale(locale)] || SUPPORTED_LOCALES[DEFAULT_LOCALE])?.locale || DEFAULT_LOCALE;
};

export const validateAsSupportedLocale = (locale) => {
  try {
    const language = new Intl.Locale(locale).language;
    return SUPPORTED_LOCALES[shortcodeToLocale(language)]?.locale;
  } catch (e) {
    return null;
  }
};

export const getSupportedBrowserLocales = () => {
  const languages = window.navigator.languages;
  return languages.reduce((supportedLocales, language) => {
    const locale = validateAsSupportedLocale(language);
    if (locale && !supportedLocales.includes(locale)) supportedLocales.push(locale);
    return supportedLocales;
  }, []);
};

export const LocalizationContextProvider = (props) => {
  const { firestore } = useContext(FirebaseContext);
  const { orgId } = useProductDataOutsideRoutes();
  const { features } = useFeatureContext();
  const { userLanguageEnabled } = features;
  const { userData: { locale: preferredUserLocale } = {}, loading: userLocaleIsLoading } = useUser();

  const { data: orgLocale, isLoading: isOrgLocaleLoading } = useQuery({
    queryKey: ['locale', orgId],
    queryFn: async () => {
      const localeSettings = await firestore.doc(`organizations/${orgId}/settings/locales`).get();
      if (!localeSettings.exists) return;

      return localeSettings.data().dashboard;
    },
    onError: (err) => {
      console.warn('Failed to get dashboard locale from organization', err);
    },
    enabled: !!orgId && (!userLanguageEnabled || (!userLocaleIsLoading && !preferredUserLocale)),
    keepPreviousData: true,
  });

  const { data: { defaultLocale: orgDefaultLocale } = {}, isLoading: orgSettingsLoading } = useOrgGeneralSettings({
    enabled: !!orgId && (!userLanguageEnabled || (!userLocaleIsLoading && !preferredUserLocale)) && !isOrgLocaleLoading && !orgLocale,
    keepPreviousData: true,
    onError: (err) => {
      console.warn('Failed to get general organization settings.', err);
    },
  });

  const { locale, languageSource } = useMemo(() => {
    if (userLanguageEnabled) {
      return [
        { locale: preferredUserLocale, languageSource: 'user' },
        { locale: orgLocale, languageSource: 'orgDashboardLanguage' },
        { locale: orgDefaultLocale, languageSource: 'orgDefault' },
        { locale: getBrowserLocale(), languageSource: 'browser' },
      ].find(({ locale: localeToValidate }) => validateAsSupportedLocale(localeToValidate)) || {};
    }
    return [
      { locale: orgLocale, languageSource: 'orgDashboardLanguage' },
      { locale: getBrowserLocale(), languageSource: 'browser' },
    ].find(({ locale: localeToValidate }) => validateAsSupportedLocale(localeToValidate)) || {};
  }, [orgLocale, preferredUserLocale, orgDefaultLocale, userLanguageEnabled]);

  const momentAndDateFns = useLocaleMomentAndDateFNS({ locale });

  const { data: translations, isLoading: isTranslationsLoading } = useQuery({
    queryKey: ['translations', locale],
    queryFn: async () => {
      const getTranslations = async (firestore, locale) => {
        const translationsCollection = firestore
          .collection('internalLocalizations')
          .doc(locale)
          .collection('translations');

        const [dashboardDoc, firebaseErrorsDoc] = await Promise.all([
          translationsCollection.doc('dashboard').get(),
          translationsCollection.doc('firebaseErrors').get(),
        ]);

        const dashboard = dashboardDoc.exists ? dashboardDoc.data().labels
          : DEFAULT_TRANSLATIONS.dashboard;
        const firebaseErrors = firebaseErrorsDoc.exists ? firebaseErrorsDoc.data().labels
          : DEFAULT_TRANSLATIONS.firebaseErrors;

        return { dashboard, firebaseErrors };
      };
      const matchingTranslationLocale = Object.values(SUPPORTED_LOCALES).find((supportedLocale) => {
        if (supportedLocale.locale === locale) return true;
        return getLanguageCodeFromLocale(supportedLocale.locale) === getLanguageCodeFromLocale(locale);
      });
      const payload = { locale, languageSource };
      trackingService.track({ eventType: 'app:load-language', payload });
      return getTranslations(firestore, matchingTranslationLocale?.locale || DEFAULT_LOCALE);
    },
    onError: (err) => {
      console.warn('Failed to fetch translations from firestore, defaulting to internal translations', { err });
    },
    enabled: !!locale && !isOrgLocaleLoading && !userLocaleIsLoading && !orgSettingsLoading,
    keepPreviousData: true,
  });

  const getText = useMemo(() => factory.getText(translations), [translations]);
  const getErrorMessage = useMemo(() => factory.getErrorMessage(translations), [translations]);
  const t = useMemo(() => factory.t(translations), [translations]);
  const localeDate = useMemo(() => factory.localeDate(momentAndDateFns?.dateFNSLocaleModule) , [momentAndDateFns?.dateFNSLocaleModule]);
  const localeDateTime = useMemo(() => factory.localeDateTime(momentAndDateFns?.dateFNSLocaleModule) , [momentAndDateFns?.dateFNSLocaleModule]);
  const localeTime = useMemo(() => factory.localeTime(momentAndDateFns?.dateFNSLocaleModule) , [momentAndDateFns?.dateFNSLocaleModule]);

  const context = {
    getText,
    getErrorMessage,
    t,
    locale,
    translations,
    localeDate,
    localeDateTime,
    localeTime,
    loading: isTranslationsLoading || isOrgLocaleLoading || userLocaleIsLoading,
    ...momentAndDateFns,
  };

  return (
    <LocalizationContext.Provider value={context}>
      {props.children}
    </LocalizationContext.Provider>
  );
};

const getLabelsDefault = () => ({});

/**
 * @template T
 * @param {(getText: (label: String, data?: any) => String) => T} getLabels - A function which resolves labels.
 */
export const useLocalization = (getLabels = getLabelsDefault) => {
  const { locale, getText, getErrorMessage, t, momentLocale, dateFNSLocale, localeDate, localeDateTime, localeTime } = useContext(LocalizationContext);

  const labels = useMemo(() => getLabels(getText), [getLabels, getText]);

  const localization = useMemo(() => {
    return {
      locale,
      language: getLanguage(locale),
      labels,
      isLatin: !SUPPORTED_LOCALES[locale]?.script,
      getErrorMessage,
      t,
      localeDate,
      localeDateTime,
      localeTime,
      momentLocale,
      dateFNSLocale,
    };
  }, [getErrorMessage, labels, locale, t, momentLocale, dateFNSLocale, localeDate, localeDateTime, localeTime]);

  return localization;
};

export const withLocalization = (getLabels = () => ({})) => Component => {
  // We need to forward refs, otherwise refs will incorrectly reference this component and not the underlying component.
  const withLocalizationComponent = React.forwardRef((props, ref) => {
    const localization = useLocalization(getLabels);

    return (
      <Component ref={ref} {...props} localization={localization} />
    );
  });

  // Give the withLocalizationComponent a displayname for easy debugging!
  withLocalizationComponent.displayName = `WithLocalization(${getDisplayName(Component)})`;

  return withLocalizationComponent;
};
